import type { PublicApiSettingsEntity } from './entity/publicApiSettingsEntity';
import type { PublicApiSettingsIRepository } from './entity/publicApiSettingsIRepository';
import type { NurseryId } from './entity/valueObjects/nurseryId';
import type { PaymentSquareApi } from '@/infra/paymentSquareApi';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';

/**
 * 支払ドメインサービス
 */
export class PaymentDomainService {
  private wagakoNurseryApi!: WagakoNurseryIApi;

  private paymentSquareApi!: PaymentSquareApi;

  private publicApiSettingsRepository!: PublicApiSettingsIRepository;

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param paymentSquareApi paymentSquareApi
   * @param publicApiSettingsRepository publicApiSettingsRepository
   */
  inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    paymentSquareApi: PaymentSquareApi,
    publicApiSettingsRepository: PublicApiSettingsIRepository,
  ) {
    this.wagakoNurseryApi = wagakoNurseryApi;
    this.paymentSquareApi = paymentSquareApi;
    this.publicApiSettingsRepository = publicApiSettingsRepository;
  }

  /**
   * getApiSettings
   * @param nurseryId 施設ID
   * @returns 結果
   */
  async getApiSettings(nurseryId: NurseryId): Promise<PublicApiSettingsEntity | undefined> {
    // Apiで読み込んでEntityとして返す
    const { items } = await this.publicApiSettingsRepository.getPublicList(nurseryId.value, null);
    if (items.length > 0) {
      return items[0];
    }
    return undefined;
  }

  /**
   * UIにカード支払いフォームを埋め込む
   * @param id id
   * @param selectorOrElement UIのID
   * @returns エラー
   */
  attachCreditCard(
    id: {
      appId: string;
      locationId: string;
    },
    selectorOrElement: string,
  ) {
    return this.paymentSquareApi.attach(id, selectorOrElement);
  }

  /**
   * クレジットカード情報をトークン化する.
   * @returns 結果
   */
  tokenizeCreditCard() {
    return this.paymentSquareApi.tokenize();
  }

  /**
   * クレジットカードを作成
   * @param nurseryId 施設ID
   * @param sourceId ソースID
   * @returns 結果
   */
  async createCreditCard(nurseryId: string, sourceId: string): Promise<{
    /** エラー */
    error: string;
    /** カードID */
    cardId: string;
  }> {
    const response = await this.wagakoNurseryApi.createCreditCard(
      nurseryId,
      { sourceId },
    );
    if (response.statusCode === 201) {
      return { error: '', cardId: response.data.cardId };
    }
    return { error: response.message, cardId: '' };
  }

  /**
   * クレジットカードリストを取得
   * @param nurseryId 施設ID
   * @returns カードリスト
   */
  async getCreditCardList(nurseryId: string): Promise<{
    /** エラー */
    error: string;
    /** カードリスト */
    cards: {
      /** ID */
      id: string;
      /** 期限月 */
      expMonth: number | undefined;
      /** 期限年 */
      expYear: number | undefined;
      /** 最後の4桁 */
      last4: string | undefined;
      /** カードブランド */
      cardBrand: string | undefined;
      /** カードの種類 */
      cardType: string | undefined;
      /** プリペイド */
      prepaidType: string | undefined;
    }[];
  }> {
    const response = await this.wagakoNurseryApi.getCreditCardList(
      nurseryId,
    );
    if (response.statusCode === 200) {
      return { error: '', cards: response.data.cards };
    }
    if (response.statusCode === 204) {
      return { error: '', cards: [] };
    }
    return { error: response.message, cards: [] };
  }
}
